import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  Divider,
  Alert,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Person, Phone, Visibility, VisibilityOff, Lock } from '@mui/icons-material';
import EmailAutocompleteTextField from '../../EmailAutocompleteTextField';

const PersonalInfoStep = ({ formData, updateFormData, errors }) => {
  const [showPassword, setShowPassword] = useState(false);

  // Phone number formatting function (same as UnifiedBookingFlow)
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Apply formatting
    if (digits.length >= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    } else if (digits.length >= 3) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return digits;
    }
  };

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Password validation function
  const validatePassword = (password) => {
    return password && password.length >= 8;
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    updateFormData({ [field]: value });
  };

  const handlePhoneChange = (event) => {
    const formatted = formatPhoneNumber(event.target.value);
    updateFormData({ phoneNumber: formatted });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };


  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Create Your Account
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Create your account to start the onboarding process. We'll collect your professional details in the next steps.
      </Typography>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person /> Account Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="First Name"
                placeholder="John"
                value={formData.firstName || ''}
                onChange={handleChange('firstName')}
                error={!!errors.firstName}
                helperText={errors.firstName || 'Your legal first name'}
                InputProps={{
                  startAdornment: <Person sx={{ color: 'action.active', mr: 1 }} />
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Last Name"
                placeholder="Doe"
                value={formData.lastName || ''}
                onChange={handleChange('lastName')}
                error={!!errors.lastName}
                helperText={errors.lastName || 'Your legal last name'}
                InputProps={{
                  startAdornment: <Person sx={{ color: 'action.active', mr: 1 }} />
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <EmailAutocompleteTextField
                fullWidth
                required
                type="email"
                label="Email Address"
                name="email"
                placeholder="john.doe@example.com"
                value={formData.email || ''}
                onChange={(value) => {
                  updateFormData({ email: value });
                }}
                error={!!errors.email}
                helperText={
                  errors.email || 
                  (formData.email && !validateEmail(formData.email) 
                    ? 'Please enter a valid email address' 
                    : 'This will be your login email and primary contact')
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type={showPassword ? 'text' : 'password'}
                label="Password"
                placeholder="Create a secure password"
                value={formData.password || ''}
                onChange={handleChange('password')}
                error={!!errors.password}
                helperText={
                  errors.password || 
                  (formData.password && !validatePassword(formData.password)
                    ? 'Password must be at least 8 characters long'
                    : 'Minimum 8 characters required')
                }
                InputProps={{
                  startAdornment: <Lock sx={{ color: 'action.active', mr: 1 }} />,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Phone Number"
                placeholder="(555) 123-4567"
                value={formData.phoneNumber || ''}
                onChange={handlePhoneChange}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber || 'For scheduling and client coordination'}
                inputProps={{ maxLength: 14 }}
                InputProps={{
                  startAdornment: <Phone sx={{ color: 'action.active', mr: 1 }} />
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  By providing your mobile number you agree to receive operational SMS from 5 Fairies Cleaning (e.g., job offers, scheduling). Msg & data rates may apply. Reply STOP to opt out, HELP for help.
                </Typography>
              </Alert>
            </Grid>


          </Grid>
        </CardContent>
      </Card>

      {/* Account Security Notice */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Account Security:</strong> Your account will be created securely and you'll be automatically logged in. 
          You can update your profile information in the following steps. We use industry-standard encryption to protect your data.
        </Typography>
      </Alert>
    </Box>
  );
};

export default PersonalInfoStep;